/**
 * Generic TypeScript utilities for inferring field types from model interfaces
 * This eliminates the need for hardcoded field type mappings in grid configurations
 */

// Extract the TypeScript type of a specific property from a model
type PropertyType<T, K extends keyof T> = T[K];

// Determine if a property type represents a date
type IsDateType<T> = T extends string | null
  ? T extends `${string}Date` | `${string}Time` | `${string}Created` | `${string}Modified` | 'dob'
    ? true
    : false
  : T extends Date | Date | null | undefined
    ? true
    : false;

// Determine if a property type represents a number
type IsNumericType<T> = T extends number | number | null | undefined ? true : false;

// Determine if a property type represents a boolean
type IsBooleanType<T> = T extends boolean | boolean | null | undefined ? true : false;

// Map TypeScript types to grid column types
type InferColumnType<T> = IsDateType<T> extends true ? 'date' : IsNumericType<T> extends true ? 'numeric' : IsBooleanType<T> extends true ? 'boolean' : 'text';

// Create a type that maps all properties of a model to their inferred column types
export type ModelFieldTypes<T> = {
  [K in keyof T]: InferColumnType<T[K]>;
};

// Runtime type inference utilities
export const ModelTypeInference = {
  /**
   * Infer the column type for a specific field from a model instance
   */
  inferFieldType<T>(model: T, field: keyof T): 'date' | 'numeric' | 'boolean' | 'text' {
    const value = model[field];
    const fieldName = String(field).toLowerCase();

    // Check actual value type first
    if (typeof value === 'boolean') {
      return 'boolean';
    }

    if (typeof value === 'number') {
      return 'numeric';
    }

    // Check for date patterns in field names
    if (/date|time|created|modified|uploaded|assigned|completed|returned|requested|dob/i.test(fieldName)) {
      return 'date';
    }

    // Check for numeric patterns in field names
    if (/id|count|number|percent|sla|tat|age|ordinal|priority/i.test(fieldName)) {
      return 'numeric';
    }

    // Check for boolean patterns in field names
    if (/active|share|qa|duplicate|abnormal|expanded|enabled|visible|readonly/i.test(fieldName)) {
      return 'boolean';
    }

    // Check if value looks like a date string
    if (typeof value === 'string' && value) {
      const datePattern = /^\d{4}-\d{2}-\d{2}|^\d{4}\/\d{2}\/\d{2}|T\d{2}:\d{2}:\d{2}/;
      if (datePattern.test(value) || !isNaN(Date.parse(value))) {
        return 'date';
      }
    }

    return 'text';
  },

  /**
   * Generate field type mapping for all fields in a model
   */
  generateFieldTypeMap<T extends Record<string, any>>(sampleModel: T): Record<keyof T, 'date' | 'numeric' | 'boolean' | 'text'> {
    const fieldTypes = {} as Record<keyof T, 'date' | 'numeric' | 'boolean' | 'text'>;

    for (const field in sampleModel) {
      fieldTypes[field] = this.inferFieldType(sampleModel, field);
    }

    return fieldTypes;
  },

  /**
   * Create human-readable labels from field names
   */
  generateFieldLabel(field: string): string {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/\bId\b/g, 'ID')
      .replace(/\bUid\b/g, 'UID')
      .replace(/\bSla\b/g, 'SLA')
      .replace(/\bTat\b/g, 'TAT')
      .replace(/\bDob\b/g, 'Date of Birth')
      .trim();
  },

  /**
   * Generate sort options dynamically from a model type
   */
  generateSortOptionsFromModel<T extends Record<string, any>>(
    sampleModel: T,
    visibleFields: (keyof T)[],
  ): Array<{
    label: string;
    field: keyof T;
    direction: 'asc' | 'desc';
    isDefault?: boolean;
  }> {
    const sortOptions: Array<{
      label: string;
      field: keyof T;
      direction: 'asc' | 'desc';
      isDefault?: boolean;
    }> = [];

    visibleFields.forEach((field) => {
      const fieldType = this.inferFieldType(sampleModel, field);
      const label = this.generateFieldLabel(String(field));

      switch (fieldType) {
        case 'date':
          sortOptions.push({ label: `${label} (Newest)`, field, direction: 'desc' }, { label: `${label} (Oldest)`, field, direction: 'asc' });
          break;
        case 'numeric':
          if (field === 'id') {
            sortOptions.unshift({ label: 'Newest First', field, direction: 'desc', isDefault: true }, { label: 'Oldest First', field, direction: 'asc' });
          } else {
            sortOptions.push({ label: `${label} (High to Low)`, field, direction: 'desc' }, { label: `${label} (Low to High)`, field, direction: 'asc' });
          }
          break;
        case 'text':
          sortOptions.push({ label: `${label} (A-Z)`, field, direction: 'asc' }, { label: `${label} (Z-A)`, field, direction: 'desc' });
          break;
        case 'boolean':
          sortOptions.push({ label: `${label} (Yes First)`, field, direction: 'desc' }, { label: `${label} (No First)`, field, direction: 'asc' });
          break;
      }
    });

    // Ensure we have a default if none was set
    if (!sortOptions.some((opt) => opt.isDefault) && sortOptions.length > 0) {
      sortOptions[0].isDefault = true;
    }

    return sortOptions;
  },
};

// Type-safe field validation
export type ValidateModelFields<TModel, TFields extends readonly (keyof TModel)[]> = TFields;

// Helper to ensure field names are valid for a given model
export const validateFields = <TModel, TFields extends readonly (keyof TModel)[]>(fields: ValidateModelFields<TModel, TFields>): TFields => fields;
